/**
 * features/auth/Login.tsx
 * LKC Academic Platform — sign-in screen.
 * Detects ?portal=parent in the URL to show a parent-specific variant.
 */
import { useState, useMemo, type FormEvent } from "react";
import {
  Loader2,
  Lock,
  Mail,
  AlertCircle,
  GraduationCap,
  Users,
  BarChart3,
  ClipboardCheck,
  Shield,
  BookOpen,
  FileText,
  Calendar,
  User,
  Eye,
  EyeOff,
  ShieldAlert,
  Clock,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { friendlyAuthError } from "@/lib/authErrors";
import { SCHOOL } from "@/lib/school.config";
import SchoolLogo from "@/components/ui/SchoolLogo";

const STAFF_FEATURES = [
  { icon: GraduationCap, label: "Student Admissions & Profiles" },
  { icon: ClipboardCheck, label: "Academic Reports & Transcripts" },
  { icon: BarChart3, label: "Attendance & Progress Tracking" },
  { icon: Users, label: "Parent Communication" },
  { icon: Shield, label: "Discipline Management" },
  { icon: BookOpen, label: "Curriculum & Assessment Config" },
];

const PARENT_FEATURES = [
  { icon: FileText, label: "View Report Cards" },
  { icon: Calendar, label: "Track Attendance" },
  { icon: User, label: "Student Profile & Subjects" },
  { icon: Shield, label: "Discipline Records" },
];

export default function Login() {
  const { login, resetPassword, accessBlocked, sessionExpired } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isParentPortal = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("portal") === "parent";
  }, []);

  const features = isParentPortal ? PARENT_FEATURES : STAFF_FEATURES;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setInfo("");
    setBusy(true);
    try {
      // Trim credentials defensively. A leading/trailing tab or space —
      // e.g. from tabbing into the field or a copy-paste that grabbed
      // adjacent whitespace — would otherwise be sent verbatim and cause
      // a spurious "incorrect password" (the auth endpoint rejects
      // "\tLKC...!" as not matching "LKC...!"). Passwords with edge
      // whitespace are never intended here.
      const cleanEmail = email.trim();
      const cleanPassword = password.trim();
      if (resetMode) {
        try {
          await resetPassword(cleanEmail);
        } catch (err) {
          // Never reveal whether an account exists (enumeration) — a
          // user-not-found / invalid-email is folded into the same neutral
          // notice below. Only surface a genuine transport/config/rate error,
          // and with a RESET-appropriate message (not "Sign-in failed").
          const code = (err as { code?: string })?.code ?? '';
          if (code !== 'auth/user-not-found' && code !== 'auth/invalid-email') {
            setError(
              friendlyAuthError(
                err,
                "Couldn't send the reset link. Please wait a few minutes and try again, or ask the school to reset your password for you.",
              ),
            );
            return;
          }
        }
        setInfo("If an account exists for that email, a reset link has been sent — check your spam folder. If it doesn't arrive, ask your administrator to reset your password for you.");
        setResetMode(false);
        return;
      }
      await login(cleanEmail, cleanPassword);
    } catch (err) {
      setError(friendlyAuthError(err, 'Sign-in failed. Please try again, or contact the school.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Brand panel */}
      <div className={`lg:w-1/2 text-white flex flex-col justify-center px-10 py-16 relative overflow-hidden ${
        isParentPortal ? 'bg-[#0b2740]' : 'bg-[#12264A]'
      }`}>
        {/* LKC brand backdrop — a woven gold chevron texture on deep navy,
            a nod to Botswana textile geometry. Deliberately distinct from
            the soft blurred-blob gradient, while staying on LKC navy + gold. */}
        <div aria-hidden className="absolute inset-0">
          {/* Base gradient */}
          <div className={`absolute inset-0 ${
            isParentPortal
              ? 'bg-[linear-gradient(120deg,#07182a,#0b2740_60%,#0d3350)]'
              : 'bg-[linear-gradient(120deg,#0a1730,#12264A_60%,#183463)]'
          }`} />
          {/* Woven gold chevrons — pixel-tiled so they stay crisp at any width,
              concentrated on the left and fading toward the form. */}
          <svg className="absolute inset-0 h-full w-full opacity-90">
            <defs>
              <pattern id="lkcChev" width="34" height="34" patternUnits="userSpaceOnUse">
                <path d="M0 34 L17 17 L34 34" fill="none" stroke="#D4A92C" strokeWidth="2" opacity="0.28" />
                <path d="M0 17 L17 0 L34 17" fill="none" stroke="#E8C55A" strokeWidth="2" opacity="0.18" />
              </pattern>
              <linearGradient id="lkcChevFade" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
                <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
              </linearGradient>
              <mask id="lkcChevMask">
                <rect width="100%" height="100%" fill="url(#lkcChevFade)" />
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="url(#lkcChev)" mask="url(#lkcChevMask)" />
          </svg>
          {/* Solid gold edge bar on the far left */}
          <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-[#E8C55A] to-[#B98E1E]" />
          {/* Thin gold rule on the seam */}
          <div className="absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-secondary/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-md mx-auto">
          <div className="flex items-center gap-4">
            <SchoolLogo size={72} />
            <div>
              <h1 className="text-2xl font-extrabold leading-tight">
                {SCHOOL.name}
              </h1>
              <p className="text-secondary font-semibold text-sm tracking-wide">
                {SCHOOL.motto} — {SCHOOL.mottoTranslation}
              </p>
            </div>
          </div>

          <div className="mt-10 mb-2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium text-white/90">
              <span className={`w-2 h-2 rounded-full animate-pulse ${isParentPortal ? 'bg-sky-400' : 'bg-secondary'}`} />
              {isParentPortal ? 'Parent Portal' : SCHOOL.platformName}
            </span>
          </div>

          <p className="mt-4 text-lg text-white/80 leading-relaxed font-medium">
            {isParentPortal
              ? "Stay connected with your child's academic journey. View reports, attendance, and more."
              : "A unified platform for academic management, student records, and institutional intelligence."}
          </p>

          {/* Feature chips */}
          <div className={`mt-8 grid ${isParentPortal ? 'grid-cols-2' : 'grid-cols-2'} gap-3`}>
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.label}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                    <Icon size={15} className={isParentPortal ? 'text-sky-300' : 'text-secondary'} />
                  </div>
                  <span className="text-xs text-white/70 leading-tight">
                    {f.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex items-center gap-4 text-xs text-white/30">
            <span>{SCHOOL.contact.addressLines.join(" · ")}</span>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="lg:w-1/2 flex items-center justify-center px-6 py-16 bg-white dark:bg-gray-900">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            {isParentPortal && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-medium mb-3">
                <Users size={12} />
                Parent / Guardian Access
              </span>
            )}
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {resetMode ? "Reset password" : isParentPortal ? "Parent Sign In" : "Welcome back"}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {resetMode
                ? "Enter your email to receive a reset link."
                : isParentPortal
                ? "Sign in to view your child's academic information."
                : `Sign in to the ${SCHOOL.platformNameShort} platform.`}
            </p>
          </div>

          {/* Signed out for inactivity. Without this the portal simply
              returns to the login form, which reads as a fault or a dropped
              connection — and the person tries again wondering what broke.

              ALNUR-PORTAL'S BANNER, EXACTLY: solid red, white text, square.
              The school chose it from three, and chose it over the softer
              notice this card uses for a wrong password. Two reasons, both
              deliberate: a tinted panel is skimmable and this message has to
              land, and the two schools should say this the same way. Solid
              and square on purpose — a notice, not another rounded control
              on the form. */}
          {sessionExpired && !error && (
            <div role="alert" className="mb-5 flex items-start gap-2.5 rounded-none border-l-4 border-white/70 bg-red-700 px-3.5 py-3 text-sm font-medium text-white shadow-lg dark:bg-red-700 dark:text-white">
              <Clock size={17} className="mt-0.5 shrink-0" />
              <span className="text-white dark:text-white">
                You were signed out because the portal was left inactive. Please sign in again.
              </span>
            </div>
          )}

          {/* Signed out because the account is deactivated. Without this the
              screen just returns to the login form, which reads as a bug or a
              forgotten password and sends the person to the office to have a
              working account "fixed". */}
          {accessBlocked && !error && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <ShieldAlert size={16} className="mt-0.5 shrink-0" />
              <span>
                This account has been deactivated and can no longer sign in.
                If you think that is a mistake, please contact the school office.
              </span>
            </div>
          )}

          {error && (
            <div className="mb-5 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {info && (
            <div className="mb-5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
              {info}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none dark:bg-gray-800 dark:border-gray-700 dark:text-white transition-colors"
                  placeholder={isParentPortal ? "parent@example.com" : "you@lkc.ac.bw"}
                />
              </div>
            </div>

            {!resetMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-10 py-2.5 rounded-xl border-2 border-gray-200 focus:border-primary focus:outline-none dark:bg-gray-800 dark:border-gray-700 dark:text-white transition-colors"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    aria-label="Press and hold to reveal password"
                    title="Press and hold to reveal"
                    // Press-and-hold: password stays visible only while
                    // the button is actively held down. Releasing the
                    // mouse, lifting the finger, or moving focus away
                    // re-masks it — so a user can never walk away and
                    // leave their password visible on screen.
                    onMouseDown={() => setShowPassword(true)}
                    onMouseUp={() => setShowPassword(false)}
                    onMouseLeave={() => setShowPassword(false)}
                    onTouchStart={() => setShowPassword(true)}
                    onTouchEnd={() => setShowPassword(false)}
                    onTouchCancel={() => setShowPassword(false)}
                    onBlur={() => setShowPassword(false)}
                    onContextMenu={(e) => e.preventDefault()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors select-none"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-60 shadow-lg ${
                isParentPortal
                  ? 'bg-gradient-to-r from-[#145374] to-[#0c2d48] hover:from-[#0c2d48] hover:to-[#145374] shadow-[#145374]/20'
                  : 'bg-gradient-to-r from-primary to-primaryDark hover:from-primaryDark hover:to-primary shadow-primary/20'
              }`}
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {resetMode ? "Send reset link" : "Sign in"}
            </button>
          </form>

          <button
            onClick={() => {
              setResetMode(!resetMode);
              setError("");
              setInfo("");
            }}
            className="mt-4 text-sm text-primary hover:underline dark:text-primaryLight"
          >
            {resetMode ? "← Back to sign in" : "Forgot your password?"}
          </button>

          {/* A parent stuck on this screen has just been told their details are
              wrong, and the next thing they do is message the school. This is
              the URL the office can paste into that reply — and unlike the
              reset above, it tells them when the address is not one we hold. */}
          {isParentPortal && !resetMode && (
            <div className="mt-2">
              <a
                href="/parent-signin-help.html"
                className="text-xs text-gray-500 hover:text-primary hover:underline dark:text-gray-400"
              >
                Still can't get in? Get help signing in →
              </a>
            </div>
          )}

          {/* Toggle between staff and parent login */}
          <div className="mt-6 text-center">
            {isParentPortal ? (
              <a
                href="/"
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Staff login →
              </a>
            ) : (
              <a
                href="/?portal=parent"
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Parent / Guardian login →
              </a>
            )}
          </div>

          <div className="mt-10 pt-6 border-t border-gray-100 dark:border-gray-800">
            <p className="text-xs text-gray-400 text-center">
              {SCHOOL.name} · {isParentPortal ? 'Parent Portal' : SCHOOL.platformName}
            </p>
            <p className="text-[10px] text-gray-300 text-center mt-1">
              {SCHOOL.contact.phonePrimary} · {SCHOOL.contact.email}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
